import ComputedData from '@/pure/ComputedData';
import { roundPercentObjectValues } from '@/pure/round-percent-object-values';

const computedData = new ComputedData();

export default {
  tutorialDone: state => !!state.tutorial,
  currentModal: state => state.currentModal,
  activeAside: state => state.activeAside,
  isochrone: state => state.isochrone,
  isochroneMode: state => state.isochroneMode,
  isochroneDecay: state => state.isochroneDecay,
  isochroneDisplay: state => state.isochroneDisplay,
  rawStudy: state => {
    if (state.rawStudy && state.rawStudy.is_draft) {
      state.rawStudy.need_refresh = true;
    }

    return state.rawStudy;
  },
  study: state => state.study,
  studies: state => state.studies,
  studyLoaded: state => state.studyLoaded,
  activeIndex: state => state.activeIndex,
  filter: state => id => state.filters[id],
  filters: state => state.filters,
  publicTransportLinesShown: state => state.publicTransportLinesShown,
  airQualityIndexOverlayShown: state => state.airQualityIndexOverlayShown,
  isFetching: state => state.isFetching,
  ruleFilters: state => state.ruleFilters,
  globalFilters: state => state.globalFilters,

  transportDistribution: (state, getters) => {
    const collaboratorsCount = getters.collaborators.length;

    const collaboratorsCustomTransport = getters.collaborators.map(c =>
      c.details.transportType
        // replace 'public2025' or 'public2030' or 'public2027' or 'public2031' to 'public'
        .replaceAll(/\d/g, '')
        // replace 'private_without_toll'                                       to 'private'
        .replaceAll('_without_toll', ''),
    );
    const customPublicRatio = (collaboratorsCustomTransport.filter(c => c === 'public').length / collaboratorsCount) * 100;
    const customPrivateRatio = (collaboratorsCustomTransport.filter(c => c === 'private').length / collaboratorsCount) * 100;
    const customBicycleRatio = (collaboratorsCustomTransport.filter(c => c === 'bicycle').length / collaboratorsCount) * 100;
    const customWalkingRatio = (collaboratorsCustomTransport.filter(c => c === 'walking').length / collaboratorsCount) * 100;

    const collaboratorsOptimizedTransport = getters.collaborators.map(c =>
      c.sites[0].optimizedSite
        // replace 'public2025' or 'public2030' or 'public2027' or 'public2031' to 'public'
        .replaceAll(/\d/g, '')
        // replace 'private_without_toll'                                       to 'private'
        .replaceAll('_without_toll', ''),
    );
    const optimizedPublicRatio = (collaboratorsOptimizedTransport.filter(c => c === 'public').length / collaboratorsCount) * 100;
    const optimizedPrivateRatio = (collaboratorsOptimizedTransport.filter(c => c === 'private').length / collaboratorsCount) * 100;
    const optimizedBicycleRatio = (collaboratorsOptimizedTransport.filter(c => c === 'bicycle').length / collaboratorsCount) * 100;
    const optimizedWalkingRatio = (collaboratorsOptimizedTransport.filter(c => c === 'walking').length / collaboratorsCount) * 100;

    return {
      custom: roundPercentObjectValues({
        public: customPublicRatio,
        car: customPrivateRatio,
        bicycle: customBicycleRatio,
        walking: customWalkingRatio,
      }),
      optimized: roundPercentObjectValues({
        public: optimizedPublicRatio,
        car: optimizedPrivateRatio,
        bicycle: optimizedBicycleRatio,
        walking: optimizedWalkingRatio,
      }),
    };
  },

  collaboratorsNotFilteredByTime: state => {
    if (!state.study) return [];

    let filteredCollaborators = state.study.collaborators.filter(item => item.address != null && item.sites.length > 0);
    for (const filter in state.filters) {
      if (state.filters[filter].length > 0) {
        filteredCollaborators = filteredCollaborators.filter(person => {
          return state.filters[filter].includes(person.details[filter]) ? person : false;
        });
      }
    }

    return filteredCollaborators;
  },
  collaboratorsFilteredByTime: (state, getters) => {
    if (!state.study) return [];

    const currentSitesOnly = state.study.sites.filter(site => site.type_id === 2).length === 0;
    if (currentSitesOnly) return getters.collaboratorsNotFilteredByTime;

    const currentTransportFilter = state.globalFilters.currentMatchingTime;
    const globalTargetSiteFilter = state.globalFilters.targetSites;
    const generalCurrentTransportFilter = currentTransportFilter
      .replaceAll(/\d/g, '') // replace 'public2025' or 'public2030' or 'public2027' or 'public2031' to 'public'
      .replaceAll('_without_toll', ''); // replace 'private_without_toll' to 'private'
    let maximumTravelTimeForCurrentTransport = state.study[generalCurrentTransportFilter + '_maximum_travel_time'];

    return getters.collaboratorsNotFilteredByTime.filter(collaborator => {
      let collaboratorTransport;
      if (currentTransportFilter === 'custom') {
        collaboratorTransport = collaborator.details.transportType;
        maximumTravelTimeForCurrentTransport = state.study[collaboratorTransport + '_maximum_travel_time'];
      }

      const allTargetSitesTooFar = globalTargetSiteFilter.every(targetSiteId => {
        const collaboratorTargetSite = collaborator.sites.find(site => site.id === targetSiteId);
        if (!collaboratorTargetSite) return true;

        if (currentTransportFilter === 'optimized') {
          collaboratorTransport = collaboratorTargetSite.optimizedSite;
          maximumTravelTimeForCurrentTransport = state.study[collaboratorTransport + '_maximum_travel_time'];
        }

        const collaboratorTimeForCurrentTransport = collaboratorTargetSite.times[0][collaboratorTransport || currentTransportFilter] || 0;

        return maximumTravelTimeForCurrentTransport < collaboratorTimeForCurrentTransport;
      });

      return !allTargetSitesTooFar;
    });
  },
  collaborators: (state, getters) =>
    state.study && state.study.withWalkingTimes && state.study.maximum_travel_time_enabled !== false
      ? getters.collaboratorsFilteredByTime
      : getters.collaboratorsNotFilteredByTime,

  site: (state, getters) => id => getters.sites.find(item => item.id === id),
  sites: state => (state.study ? (state.study.sites ? state.study.sites.sort((a, b) => a.order - b.order) : []) : []),
  currentSites: (state, getters) => getters.sites.filter(site => site.type_id === 1),
  sitesByType: (state, getters) => type => getters.sites.filter(site => site.type === type),
  screenData: (state, getters) => {
    const start = performance.now();

    const reduced = computedData.reduceData(getters.collaborators, getters.study, getters.globalFilters);

    console.log(`Compute duration: ${Math.floor(performance.now() - start)}ms`);
    return reduced;
  },
  screen: (state, getters) => () => getters.screenData,
  poiVisibility: state => state.poiVisibility,
  poiVisibilityKey: state => Object.keys(state.poiVisibility).filter(x => state.poiVisibility[x] === true),
  poiVisibilityStatus: state => type => state.poiVisibility[type],
  isDuplicating: state => state.isDuplicating,
};
